import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import SafeIcon from '../common/SafeIcon';
import * as LuIcons from 'react-icons/lu';

const { LuPhone, LuCircleCheck, LuSend, LuMapPin, LuMail } = LuIcons;

const ContactSection = () => {
  const { t, i18n } = useTranslation();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);

  const toggleService = (id) => {
    setSelectedServices(prev => 
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitted(true);
  };

  const servicesList = t('contact.options', { returnObjects: true });

  return (
    <section id="contact" className="section-container">
      <div className="section-header text-center mb-12">
        <h2 className="section-title">{t('contact.title')}</h2>
      </div>
      
      <div className="book-grid">
        <motion.div 
          className="booking-info glass-card flex flex-col h-full" 
          initial={{ opacity: 0, x: -20 }} 
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
        >
          <h3 className="text-2xl font-bold mb-6">{t('contact.infoTitle')}</h3>
          
          <div className="contact-methods mb-10">
            <a href={`tel:${t('contact.phoneVal')}`} className="contact-method-box">
              <div className="icon-circle">
                <SafeIcon icon={LuPhone} />
              </div>
              <div>
                <strong>{t('contact.phoneVal')}</strong>
              </div>
            </a>

            <a 
              href="https://www.google.com/maps/search/?api=1&query=Prästängsvägen+11+Strömstad" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="contact-method-box"
            >
              <div className="icon-circle">
                <SafeIcon icon={LuMapPin} />
              </div>
              <div>
                <strong>{t('contact.address')}</strong>
              </div>
            </a>

            <a href={`mailto:${t('contact.emailVal')}`} className="contact-method-box">
              <div className="icon-circle">
                <SafeIcon icon={LuMail} />
              </div>
              <div>
                <strong>{t('contact.emailVal')}</strong>
              </div>
            </a>
          </div>
        </motion.div>

        <motion.div 
          className="booking-form glass-card" 
          initial={{ opacity: 0, x: 20 }} 
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
        >
           <iframe
                key={i18n.language}
                title="BMI Verkstad360 Contact Form"
                src={i18n.language === 'en' ? 'https://form.bmi-software.gr/cqQKfv' : 'https://form.bmi-software.gr/Z05sbC'}
                width="100%"
                height="600"
                style={{ border: 'none', borderRadius: '8px', backgroundColor: 'rgba(221, 219, 230, 0.03)' }}
                frameBorder="0"
                scrolling="auto"
                allowTransparency="true"
                aria-hidden="false"
                tabIndex={0}
              ></iframe>
        </motion.div>
      </div>

      <style jsx>{`
        .section-title {
          font-size: 3rem;
          font-weight: 800;
        }

        .book-grid {
          display: grid;
          grid-template-columns: 1fr 1.8fr;
          gap: 2rem;
          max-width: 1200px;
          margin: 0 auto;
        }

        .booking-info h3 {
          font-size: 1.8rem;
          margin-bottom: 1rem;
        }

        /* Reduced padding for the form container to fit the iframe better */
        .booking-form {
          padding: 1.25rem;
        }

        .contact-methods {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .contact-method-box {
          display: flex;
          align-items: center;
          gap: 1.25rem;
          padding: 1.25rem;
          background: rgba(131, 119, 190, 0.05);
          border-radius: 16px;
          text-decoration: none;
          border: 1px solid rgba(131, 119, 190, 0.15);
          transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .contact-method-box:hover {
          background: rgba(131, 119, 190, 0.12);
          border-color: rgba(131, 119, 190, 0.4);
          transform: translateX(5px);
        }

        .icon-circle {
          width: 48px;
          height: 48px;
          background: rgba(131, 119, 190, 0.2);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #8377BE;
          font-size: 1.2rem;
          flex-shrink: 0;
        }

        .contact-method-box span {
          display: block;
          font-size: 0.8rem;
          color: #848489;
          margin-bottom: 2px;
        }

        .contact-method-box strong {
          font-size: 1.05rem;
          color: white;
          font-weight: 600;
        }

        @media (max-width: 868px) {
          .book-grid {
            grid-template-columns: 1fr;
          }
          .section-title {
            font-size: 2.2rem;
          }
          .booking-info h3 {
            font-size: 1.5rem;
          }
        }

        @media (max-width: 480px) {
          .section-title { font-size: 1.9rem; }
          .contact-method-box { padding: 1rem; }
          .contact-method-box strong { font-size: 0.92rem; word-break: break-all; }
          .icon-circle { width: 40px; height: 40px; flex-shrink: 0; }
          .booking-form { padding: 0.75rem; }
        }
      `}</style>
    </section>
  );
};

export default ContactSection;